import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, Container, Snackbar, Alert } from '@mui/material';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AddRoom from './pages/AddRoom';
import Navbar from './components/Navbar';
import AdminPanel from "./pages/AdminPanel";
import Reports from './pages/Reports';
import RhinoWelcome from './components/RhinoWelcome';

const API_BASE = process.env.REACT_APP_API_URL || '';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
    },
    secondary: {
      main: '#0d47a1',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [wsConnected, setWsConnected] = useState(false);
  const [rhinoDone, setRhinoDone] = useState(false);
  const stompClientRef = useRef(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Auto-subscribe to push notifications after login
  const autoSubscribePush = useCallback(async () => {
    // Check browser support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[Push] Browser does not support push notifications');
      return;
    }

    // Don't ask again if already denied
    if (Notification.permission === 'denied') {
      console.log('[Push] Notifications are blocked by user');
      return;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        // Already subscribed, just make sure backend knows
        const subJson = existingSubscription.toJSON();
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE}/api/push/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subJson.endpoint,
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth
          })
        });
        console.log('[Push] Re-registered existing subscription with backend');
        return;
      }

      // Get VAPID key
      const token = localStorage.getItem('token');
      const vapidResponse = await fetch(`${API_BASE}/api/push/vapid-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!vapidResponse.ok) return;
      const { publicKey } = await vapidResponse.json();

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      const subJson = subscription.toJSON();

      // Send to backend
      await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          p256dh: subJson.keys.p256dh,
          auth: subJson.keys.auth
        })
      });

      console.log('[Push] Auto-subscribed to push notifications');
    } catch (err) {
      console.log('[Push] Auto-subscribe failed (non-blocking):', err.message);
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    // Auto-subscribe to push after login
    autoSubscribePush();

    const client = new Client({
      webSocketFactory: () => {
        const token = localStorage.getItem('token');
        return new SockJS(`/ws?token=${token}`);
      },
      // webSocketFactory: () => new SockJS('/ws'),
      reconnectDelay: 5000,
      onConnect: () => {
        setWsConnected(true);
        console.log('WebSocket connected');

        // Subscribe pentru notificări vibrație/sunet
        client.subscribe('/topic/rooms', (message) => {
          const updatedRoom = JSON.parse(message.body);
          if (updatedRoom.status === 'DIRTY') {
            // Vibrație
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            // Sunet scurt
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = 880;
              gain.gain.value = 0.3;
              osc.start();
              osc.stop(ctx.currentTime + 0.3);
            } catch (e) {}
          }
        });
      },
      onDisconnect: () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        setWsConnected(false);
      },
    });

    client.activate();
    stompClientRef.current = client;

    return () => {
      if (client.active) {
        client.deactivate();
      }
    };
  }, [user, autoSubscribePush]);

  const handleLogin = (userData) => {
    setRhinoDone(false);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setRhinoDone(false);
    if (stompClientRef.current?.active) {
      stompClientRef.current.deactivate();
    }
  };

  if (!user) {
    return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Login onLogin={handleLogin} />
        </ThemeProvider>
    );
  }

  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* 🦏 Easter egg — apare doar pentru Jessica */}
        {!rhinoDone && (
            <RhinoWelcome user={user} onComplete={() => setRhinoDone(true)} />
        )}
        <Router>
          <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Navbar user={user} onLogout={handleLogout} wsConnected={wsConnected} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Routes>
                <Route
                    path="/"
                    element={
                      <Dashboard
                          user={user}
                          stompClient={stompClientRef.current}
                          showToast={showToast}
                      />
                    }
                />
                <Route
                    path="/add-room"
                    element={
                      user.role === 'ADMIN' || user.role === 'MANAGER'
                          ? <AddRoom showToast={showToast} />
                          : <Navigate to="/" />
                    }
                />
                <Route
                    path="/admin"
                    element={
                      user.role === 'ADMIN'
                          ? <AdminPanel showToast={showToast} />
                          : <Navigate to="/" />
                    }
                />
                <Route path="/login" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" />} />
                <Route
                    path="/reports"
                    element={
                      user.role === 'ADMIN' || user.role === 'MANAGER'
                          ? <Reports />
                          : <Navigate to="/" />
                    }
                />
              </Routes>
            </Container>
          </Box>
          <Snackbar
              open={toast.open}
              autoHideDuration={4000}
              onClose={() => setToast({ ...toast, open: false })}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
                onClose={() => setToast({ ...toast, open: false })}
                severity={toast.severity}
                variant="filled"
                sx={{ borderRadius: 2, fontWeight: 500 }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Router>
      </ThemeProvider>
  );
}

export default App;